/**
 * Manual poster mappings for movies with known poster issues
 * This helps provide better image availability for specific problematic movies
 */

const MANUAL_POSTER_MAPPINGS = {
  // Radio Pirates 2014 - known to have poster on mdblist but not on TMDB
  '207933': {
    title: 'Radio Pirates',
    year: 2014,
    poster_urls: [
      'https://image.tmdb.org/t/p/w500/aQvJ5WPzZgYVDrxLX4R6cLJCEaQ.jpg', // Known working URL
      'https://image.tmdb.org/t/p/w342/aQvJ5WPzZgYVDrxLX4R6cLJCEaQ.jpg',
      'https://image.tmdb.org/t/p/original/aQvJ5WPzZgYVDrxLX4R6cLJCEaQ.jpg'
    ],
    backdrop_urls: [
      'https://image.tmdb.org/t/p/w1280/7u3dtNGqIJN7BkK9xOGJ2aTLZR4.jpg'
    ]
  },
  // Add more problematic movies here as they're discovered
  // '123456': { ... }
};

/**
 * Get manual poster URLs for a specific movie
 * @param {string|number} movieId - TMDB movie ID
 * @param {string} movieTitle - Movie title for verification
 * @param {number} releaseYear - Release year for verification
 * @returns {Object|null} Poster and backdrop URLs or null if not found
 */
export function getManualPosterMapping(movieId, movieTitle = '', releaseYear = null) {
  const id = String(movieId);
  const mapping = MANUAL_POSTER_MAPPINGS[id];
  
  if (!mapping) return null;
  
  // Basic verification to ensure we're mapping the right movie
  const titleMatch = movieTitle.toLowerCase().includes(mapping.title.toLowerCase());
  const yearMatch = !releaseYear || releaseYear === mapping.year;
  
  if (titleMatch && yearMatch) {
    return {
      poster_urls: mapping.poster_urls || [],
      backdrop_urls: mapping.backdrop_urls || []
    };
  }
  
  return null;
}

/**
 * Check if a movie has a manual poster mapping
 * @param {string|number} movieId - TMDB movie ID
 * @returns {boolean} True if manual mapping exists
 */
export function hasManualPosterMapping(movieId) {
  return MANUAL_POSTER_MAPPINGS.hasOwnProperty(String(movieId));
}

/**
 * Get all available poster sources for a movie, including manual mappings
 * @param {Object} movie - Movie object with id, title, poster_path, etc.
 * @returns {Array} Array of poster URLs to try
 */
export function getAllPosterSources(movie) {
  const sources = [];
  
  if (!movie) return sources;
  
  // Try manual mapping first
  const manual = getManualPosterMapping(
    movie.id, 
    movie.title, 
    movie.release_date ? new Date(movie.release_date).getFullYear() : null
  );
  
  if (manual && manual.poster_urls) {
    sources.push(...manual.poster_urls);
  }
  
  // Add standard TMDB sources if poster_path exists
  if (movie.poster_path) {
    const tmdbSizes = ['w500', 'w342', 'w185', 'original'];
    tmdbSizes.forEach(size => {
      sources.push(`https://image.tmdb.org/t/p/${size}${movie.poster_path}`);
    });
  }
  
  // Remove duplicates while preserving order
  return [...new Set(sources)];
}

/**
 * Get all available backdrop sources for a movie, including manual mappings
 * @param {Object} movie - Movie object with id, title, backdrop_path, etc.
 * @returns {Array} Array of backdrop URLs to try
 */
export function getAllBackdropSources(movie) {
  const sources = [];
  
  if (!movie) return sources;
  
  // Try manual mapping first
  const manual = getManualPosterMapping(
    movie.id, 
    movie.title, 
    movie.release_date ? new Date(movie.release_date).getFullYear() : null
  );
  
  if (manual && manual.backdrop_urls) {
    sources.push(...manual.backdrop_urls);
  }
  
  // Add standard TMDB sources if backdrop_path exists
  if (movie.backdrop_path) {
    const tmdbSizes = ['w1280', 'w780', 'w500', 'original'];
    tmdbSizes.forEach(size => {
      sources.push(`https://image.tmdb.org/t/p/${size}${movie.backdrop_path}`);
    });
  }
  
  // Remove duplicates while preserving order
  return [...new Set(sources)];
}

const posterFallbacks = {
  getManualPosterMapping,
  hasManualPosterMapping,
  getAllPosterSources,
  getAllBackdropSources
};

export default posterFallbacks; 